import { useEffect, useRef, useState } from "react";
import { cn } from "../cn";

export interface CopyButtonProps {
  value: string;
  /** The accessible name — "Copy the client id", not just "Copy". */
  label: string;
  className?: string;
}

const REVERT_AFTER_MS = 1500;

/**
 * An icon-sized button that puts `value` on the clipboard.
 *
 * Three states, like every other copy control in the console (`Snippet`,
 * `CopyPath`, `CopyPlayerId`): idle, copied, failed. This one reverts itself
 * after a beat rather than waiting for a second click, because it sits beside
 * short-lived values — an id in a row, a key in a table — where a button
 * stuck on "Copied" until pressed again reads as broken rather than done.
 * The write is inside the click handler and its rejection is caught, so a
 * denied clipboard permission or an insecure context never throws past this
 * component.
 */
export function CopyButton({ value, label, className }: CopyButtonProps) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), REVERT_AFTER_MS);
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}
      title={label}
      className={cn(
        "grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] border border-rule text-ink-3",
        "transition-colors hover:bg-surface-2 hover:text-ink-2",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        state === "failed" && "border-critical text-critical-text",
        className,
      )}
    >
      {state === "copied" ? <CheckMark /> : state === "failed" ? <FailMark /> : <CopyMark />}
    </button>
  );
}

/** 16px stroke marks on a 24px grid, at 1.8, in `currentColor` — house rules §8. */
function CopyMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h8" />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FailMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
